/**
 * Sign in, and the second factor when one is demanded.
 *
 * One screen for both steps rather than two routes, because the challenge id
 * is held in memory: a navigation that reloaded the page would lose it, and
 * the user would be dropped back to the start with no explanation. The step is
 * a rendering decision, not a location.
 */

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { z } from "zod";
import { Alert, Button, Card, Input } from "@hardauto/core-ui";
import { Field, applyServerErrors } from "@hardauto/core-forms";
import { ApiError, NetworkError } from "@hardauto/core-api";

import { useSession } from "../session/session";
import type { ApiClient } from "@hardauto/core-api";

import { NAMESPACE } from "../i18n";
import { SocialButtons } from "../social/SocialButtons";

const schema = z.object({
  // Deliberately not a strict e-mail pattern: the server is the authority, and
  // a client-side rule that rejects a valid address the server would accept is
  // a user locked out by a regular expression.
  email: z.string().min(1),
  password: z.string().min(1),
});

type Values = z.infer<typeof schema>;

export interface SignInScreenProps {
  /** Where to go once signed in. Read from `returnTo` by the route. */
  onSignedIn: () => void;
  /** Links out to registration and recovery, supplied by the app's router. */
  registerHref?: string;
  forgotPasswordHref?: string;
  /**
   * The API client, for capability discovery.
   *
   * Optional, and its absence hides the social block entirely. That is the
   * right default for a screen rendered in a test or a story: a component
   * that fetched from a client it was not given would either crash or invent
   * one, and neither is better than showing the password form alone.
   */
  client?: ApiClient;
}

export function SignInScreen({
  onSignedIn,
  registerHref,
  forgotPasswordHref,
  client,
}: SignInScreenProps) {
  const { t } = useTranslation(NAMESPACE);
  const session = useSession();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  if (session.status === "mfa_required" && session.challenge) {
    return <MfaStep onVerified={onSignedIn} />;
  }

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const result = await session.signIn(values.email, values.password);
      if (result === "authenticated") onSignedIn();
    } catch (error) {
      setFormError(describe(error, t));
      if (error instanceof ApiError) {
        // Field-level messages land on their fields; anything the form has no
        // field for stays in the banner rather than vanishing.
        applyServerErrors(error, form.setError, ["email", "password"]);
      }
    }
  });

  return (
    <Card title={t("signIn.title")} description={t("signIn.description")}>
      <form onSubmit={(event) => void submit(event)} noValidate>
        {formError ? (
          <Alert tone="danger" title={t("signIn.failed")}>
            {formError}
          </Alert>
        ) : null}

        <Field label={t("field.email")} error={form.formState.errors.email?.message} required>
          {(props) => (
            <Input {...props} {...form.register("email")} type="email" autoComplete="username" />
          )}
        </Field>

        <Field label={t("field.password")} error={form.formState.errors.password?.message} required>
          {(props) => (
            <Input
              {...props}
              {...form.register("password")}
              type="password"
              autoComplete="current-password"
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          busy={form.formState.isSubmitting}
          busyLabel={t("signIn.submitting")}
        >
          {t("signIn.submit")}
        </Button>
      </form>

      {/* Below the form, not above it. The password field is what most
          people are here for, and putting three provider buttons first
          asks everybody to read past them. */}
      {client ? <SocialButtons client={client} /> : null}

      <p className="hardauto-form__links">
        {forgotPasswordHref ? <a href={forgotPasswordHref}>{t("signIn.forgot")}</a> : null}
        {registerHref ? <a href={registerHref}>{t("signIn.register")}</a> : null}
      </p>
    </Card>
  );
}

const codeSchema = z.object({
  // Trimmed and stripped of the spaces authenticator apps display, because
  // "123 456" is what the user reads and pasting it should simply work.
  code: z
    .string()
    .transform((value) => value.replace(/\s/g, ""))
    .pipe(z.string().min(4).max(12)),
});

type CodeValues = { code: string };

function MfaStep({ onVerified }: { onVerified: () => void }) {
  const { t } = useTranslation(NAMESPACE);
  const session = useSession();
  const [formError, setFormError] = useState<string | null>(null);
  const challenge = session.challenge;
  const codeInput = useRef<HTMLInputElement | null>(null);

  /**
   * Move focus into the code field when this step appears.
   *
   * NOT the same thing as `autoFocus` on the sign-in form, which is why the
   * a11y rule forbids one and not the other. There, focus starts at the top of
   * a fresh page and taking it into a field skips the heading that says where
   * the user is. Here the content the user was focused on has just been
   * replaced by their own submission, so focus is already stranded on
   * `<body>` — and moving it to the one field they must now fill in is the
   * repair, not a shortcut.
   */
  useEffect(() => {
    codeInput.current?.focus();
  }, []);

  const form = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: "" },
  });
  const codeField = form.register("code");

  if (!challenge) return null;

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await session.verifyMfa(values.code);
      onVerified();
    } catch (error) {
      setFormError(describe(error, t));
      form.reset({ code: "" });
    }
  });

  return (
    <Card
      title={t("mfa.title")}
      description={t(`mfa.prompt.${challenge.method}`, {
        defaultValue: t("mfa.prompt.generic"),
        target: challenge.sentTo ?? "",
      })}
    >
      <form onSubmit={(event) => void submit(event)} noValidate>
        {formError ? (
          <Alert tone="danger" title={t("mfa.failed")}>
            {formError}
          </Alert>
        ) : null}

        <Field label={t("field.code")} error={form.formState.errors.code?.message} required>
          {(props) => (
            <Input
              {...props}
              {...codeField}
              ref={(element) => {
                codeField.ref(element);
                codeInput.current = element;
              }}
              // `one-time-code` is what lets a phone offer the SMS or
              // authenticator code above the keyboard. Without it the user
              // types six digits by hand.
              autoComplete="one-time-code"
              // `numeric`, not `tel`: `tel` offers a dialpad with `+` and `*`,
              // which are not part of any code here.
              inputMode="numeric"
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          busy={form.formState.isSubmitting}
          busyLabel={t("mfa.submitting")}
        >
          {t("mfa.submit")}
        </Button>
      </form>

      {challenge.available.length > 1 ? (
        <div className="hardauto-form__alternatives">
          <p>{t("mfa.useAnother")}</p>
          <ul>
            {challenge.available
              .filter((method) => method.method !== challenge.method)
              .map((method) => (
                <li key={method.id}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void session.switchMfa(method.id);
                    }}
                  >
                    {t(`mfa.method.${method.method}`, { defaultValue: method.method })}
                  </Button>
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      <Button variant="ghost" size="sm" onClick={session.cancelMfa}>
        {t("mfa.cancel")}
      </Button>
    </Card>
  );
}

/**
 * Turn a failure into something worth reading.
 *
 * Branching on `type` and never on `message`: types are contract, messages are
 * translated and reworded. The fallback uses the server's message rather than
 * a generic apology, because a specific server message is more useful than
 * "something went wrong" — but it is only reached for types this screen does
 * not know, which is exactly when the server knows more than we do.
 */
function describe(error: unknown, t: TFunction<typeof NAMESPACE>): string {
  if (error instanceof NetworkError) return t("error.offline");
  if (!(error instanceof ApiError)) return t("error.unexpected");

  switch (error.type) {
    case "invalid_credentials":
      // Identical for a wrong password and an unknown address, on purpose:
      // telling them apart lets anyone test which addresses have accounts.
      return t("signIn.invalidCredentials");
    case "rate_limited":
      return t("error.rateLimited", { seconds: error.retryAfterSeconds ?? 60 });
    case "tier_disabled":
      return t("signIn.accountDisabled");
    case "mfa_challenge_invalid":
      // Covers a wrong code, an expired challenge and an unknown id — the
      // server returns one error for all three so that a caller cannot confirm
      // a live challenge id. The wording has to be honest about that: naming
      // one cause would send the user to fix the wrong thing most of the time.
      return t("mfa.codeRejected");
    default:
      return error.message;
  }
}
